import { useState } from 'react';
import { ErroreApi } from '../api/cliente.js';
import {
  type EsitoMultiplo,
  useCreaDocumentiMultipli,
  useOpzioniDocumenti,
} from '../api/documenti.js';
import { Modale } from '../componenti/Modale.js';
import { ZonaFile } from '../componenti/ZonaFile.js';
import { t } from '../i18n/lingua.js';

/**
 * Un file, molti ordini.
 *
 * È il caso che si presenta ogni giorno: arriva un DDT che riguarda venti
 * ordini, e nel v2 si caricherebbe venti volte. Qui si carica una volta e
 * nascono venti documenti, uno per ordine, **tutti sullo stesso file**: i
 * byte non si duplicano.
 *
 * Gli ordini si indicano col numero del cliente — il PO# scritto sul
 * documento — e si aggiungono uno alla volta con Invio, come nel v2. Quelli
 * che non esistono non fermano gli altri: si creano i documenti che si
 * possono creare e si dice quali numeri sono rimasti fuori.
 */
export function ModaleDocumentiMultipli({ onChiudi }: { onChiudi: () => void }) {
  const crea = useCreaDocumentiMultipli();
  const opzioni = useOpzioniDocumenti();

  const [campi, setCampi] = useState({ title: '', protocolNumber: '', description: '' });
  const [numeri, setNumeri] = useState<string[]>([]);
  const [inCorsoNumero, setInCorsoNumero] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [categorie, setCategorie] = useState<number[]>([]);
  const [esito, setEsito] = useState<EsitoMultiplo | null>(null);

  const cambia = (chiave: keyof typeof campi, valore: string) =>
    setCampi((c) => ({ ...c, [chiave]: valore }));

  /** Un numero per volta: Invio lo aggiunge, la virgola pure. */
  const aggiungiNumero = () => {
    const puliti = inCorsoNumero
      .split(/[\s,;]+/)
      .map((n) => n.trim())
      .filter(Boolean);
    if (puliti.length === 0) return;
    setNumeri((attuali) => [...new Set([...attuali, ...puliti])]);
    setInCorsoNumero('');
  };

  const errore = crea.error instanceof ErroreApi ? crea.error.message : null;
  const pronto = campi.title.trim() !== '' && numeri.length > 0;

  const conferma = () =>
    crea.mutate(
      {
        title: campi.title.trim(),
        protocolNumber: campi.protocolNumber.trim() || null,
        description: campi.description.trim() || null,
        categoryIds: categorie,
        orderNumbers: numeri,
        file,
      },
      { onSuccess: setEsito },
    );

  const tutte = [...(opzioni.data?.categories ?? []), ...(opzioni.data?.categories2 ?? [])];

  // finito: si dice cosa è successo, compresi gli ordini che non esistevano
  if (esito) {
    return (
      <Modale
        titolo={t('Documenti creati')}
        larga
        onChiudi={onChiudi}
        azioni={
          <button className="btn btn-primary" onClick={onChiudi}>
            {t('Chiudi')}
          </button>
        }
      >
        <p style={{ marginTop: 0 }}>
          {t('Creati')} <strong>{esito.created.length}</strong>{' '}
          {esito.created.length === 1 ? 'documento' : 'documenti'}
          {esito.fileName ? (
            <>
              {t(', tutti sul file')} <span className="num">{esito.fileName}</span> {t('— caricato una volta sola.')}
            </>
          ) : (
            ', senza file.'
          )}
        </p>

        {esito.unknownOrders.length > 0 && (
          <div className="avviso" role="alert">
            <span className="avviso-segno">▲</span>
            <span>
              {t('Questi numeri non corrispondono a nessun ordine e sono rimasti fuori:')}{' '}
              <span className="num">{esito.unknownOrders.join(', ')}</span>
            </span>
          </div>
        )}

        <ul className="doclist" style={{ marginTop: 12 }}>
          {esito.created.map((d) => (
            <li key={d.id}>
              <span className="num">PO# {d.orderNumber}</span>
            </li>
          ))}
        </ul>
      </Modale>
    );
  }

  return (
    <Modale
      titolo={t('Un file, più ordini')}
      nota={t('Il file si carica una volta sola: nasce un documento per ogni ordine, tutti sullo stesso file.')}
      larga
      inCorso={crea.isPending}
      onChiudi={onChiudi}
      azioni={
        <>
          <button className="btn" onClick={onChiudi} disabled={crea.isPending}>
            {t('Annulla')}
          </button>
          <button
            className="btn btn-primary"
            onClick={conferma}
            disabled={!pronto || crea.isPending}
          >
            {crea.isPending
              ? 'Creo…'
              : numeri.length === 1
                ? t('Crea 1 documento')
                : numeri.length === 0
                  ? t('Crea i documenti')
                  : t('Crea {n} documenti', { n: numeri.length })}
          </button>
        </>
      }
    >
      {errore && (
        <div className="avviso" role="alert" style={{ marginBottom: 12 }}>
          <span className="avviso-segno">▲</span>
          <span>{errore}</span>
        </div>
      )}

      <div className="fields">
        <div className="field span-2">
          <label htmlFor="dm-titolo">
            {t('Titolo')}<span className="req">*</span>
            <span className="dim" style={{ fontWeight: 400 }}>
              {' '}
              {t('— a ognuno viene aggiunto in coda il suo PO#')}
            </span>
          </label>
          <input
            id="dm-titolo"
            className="input"
            placeholder={t('DDT USCITA MERCE 08/08/2026')}
            value={campi.title}
            autoFocus
            onChange={(e) => cambia('title', e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="dm-protocollo">{t('Numero di protocollo')}</label>
          <input
            id="dm-protocollo"
            className="input"
            value={campi.protocolNumber}
            onChange={(e) => cambia('protocolNumber', e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="dm-descrizione">{t('Descrizione')}</label>
          <input
            id="dm-descrizione"
            className="input"
            value={campi.description}
            onChange={(e) => cambia('description', e.target.value)}
          />
        </div>
      </div>

      <div style={{ margin: '14px 0' }}>
        <label className="etichetta-sezione">{t('File da caricare')}</label>
        <ZonaFile
          file={file}
          estensioni={opzioni.data?.extensions}
          massimo={opzioni.data?.maxUploadBytes}
          onFile={setFile}
        />
      </div>

      <div className="field span-2" style={{ marginBottom: 14 }}>
        <label htmlFor="dm-po">
          {t('Ordini a cui associarlo')}<span className="req">*</span>
          <span className="dim" style={{ fontWeight: 400 }}>
            {' '}
            {t('— scrivi il numero d’ordine del cliente e premi Invio')}
          </span>
        </label>
        <input
          id="dm-po"
          className="input"
          placeholder="4507377783"
          value={inCorsoNumero}
          onChange={(e) => setInCorsoNumero(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              aggiungiNumero();
            }
          }}
          onBlur={aggiungiNumero}
        />
        {numeri.length > 0 && (
          <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
            {numeri.map((n) => (
              <button
                key={n}
                className="chip chip-set"
                title={t('Togli questo ordine')}
                onClick={() => setNumeri((attuali) => attuali.filter((x) => x !== n))}
              >
                PO# {n} ✕
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="field span-2">
        <label>{t('Categorie')}</label>
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          {tutte.map((c) => (
            <button
              key={c.id}
              className={categorie.includes(c.id) ? 'chip chip-set' : 'chip'}
              onClick={() =>
                setCategorie((attuali) =>
                  attuali.includes(c.id)
                    ? attuali.filter((x) => x !== c.id)
                    : [...attuali, c.id],
                )
              }
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>
    </Modale>
  );
}
